import { Fragment, memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { chartAlpha, seriesColor } from '../constants/chartPalette';
import {
  ChartDataPoint,
  colourForToken,
  enterAt,
  formatRatio,
  formatValue,
  shareOfPeak,
  WidgetPeriodChip,
} from '../shared';

/**
 * The widest a cell is allowed to get.
 *
 * Cells used to be `1fr` with an aspect ratio, so a 2×2 grid drew four slabs
 * the size of a card and a 6×10 drew nothing legible. A heat map is a *grid*:
 * the cell is a unit of comparison, not a panel, and it stays the same size
 * whether there are four of them or sixty. `minmax(0, …)` keeps it shrinking
 * in a narrow slot rather than forcing the widget to scroll.
 */
const CELL_MAX_PX = 30;

/** How a cell's share of peak reads in words. */
const bandFor = (share) =>
  share >= 0.8 ? 'Peak' : share >= 0.5 ? 'Busy' : share >= 0.3 ? 'Steady' : 'Quiet';

/**
 * Density across two dimensions.
 *
 * Cells are shaded against the **largest value in the data**, not against a
 * fixed ceiling, so the darkest cell is always the hotspot no matter what
 * scale the numbers are on. Alpha runs 0.12–0.92: the floor is what keeps an
 * empty cell visible as a cell rather than as a hole in the grid.
 *
 * Two layouts:
 *
 * - `matrix` (default) — row labels down the left, column labels underneath.
 *   The usual two-dimension read: hours against days.
 * - `calendar` — column labels only, cells square. For a single repeating
 *   period where the rows are weeks and naming them adds nothing.
 *
 * A shaded square says *nothing* on its own about what darker means, so every
 * cell carries its band and its share of peak on hover. That tooltip is the
 * whole reason the shading is legible.
 *
 * @param {object} props
 * @param {{ columns: string[], rows?: string[], values: number[][] }} props.data Values are row-major.
 * @param {'matrix'|'calendar'} [props.variant='matrix']
 * @param {boolean} [props.showValues=true] The contract's `display.show_values`.
 * @param {string} [props.accent] Overrides the shading hue.
 * @param {string} [props.className]
 */
/*
  Two types, two shapes, one grid (CT-5641).

  `HEATMAP` publishes `cells[{x, y, value}]` with `x_categories` and
  `y_categories` — a **sparse** list, because a matrix with holes is normal and
  sending the holes would be sending nothing. `USAGE_MAP` publishes
  `cells[{key, label, group, value, limit, utilization_pct, color_token}]`,
  which is a different reading: not how big a number is against the peak, but
  how full it is against its own quota.

  Both pivot into the dense grid the drawing wants. A missing cell stays
  missing — drawn as the empty track rather than as a zero, which would read as
  "measured and found to be none".
*/
function HeatMapWidget({ data, variant = 'matrix', showValues = true, accent, className = '' }) {
  const grid = useMemo(() => {
    if (!data) return null;

    // The pre-v2 dense shape.
    if (Array.isArray(data.values)) {
      return {
        columns: data.columns ?? [],
        rows: data.rows ?? [],
        cells: data.values.flatMap((row, y) => row.map((value, x) => ({ x, y, value }))),
      };
    }

    const cells = data.cells ?? [];
    if (!cells.length) return null;

    // `USAGE_MAP` has no axes: its cells are resources, optionally grouped.
    if (!data.x_categories) {
      const groups = [...new Set(cells.map((cell) => cell.group ?? ''))];
      const labels = [...new Set(cells.map((cell) => cell.label ?? cell.key))];
      return {
        columns: labels,
        rows: groups.filter(Boolean),
        cells: cells.map((cell) => ({
          x: labels.indexOf(cell.label ?? cell.key),
          y: Math.max(0, groups.indexOf(cell.group ?? '')),
          value: cell.value,
          // A quota turns the cell's shade into "how full", which is the
          // question a usage map asks.
          ratio: typeof cell.utilization_pct === 'number' ? cell.utilization_pct : null,
          limit: cell.limit,
          token: cell.color_token,
          label: cell.label ?? cell.key,
        })),
      };
    }

    const xs = data.x_categories ?? [];
    const ys = data.y_categories ?? [];
    return {
      columns: xs,
      rows: ys,
      cells: cells.map((cell) => ({
        x: xs.indexOf(cell.x),
        y: Math.max(0, ys.indexOf(cell.y)),
        value: cell.value,
        ratio: null,
        token: null,
        label: `${cell.y} ${cell.x}`.trim(),
      })),
    };
  }, [data]);

  const columns = grid?.columns ?? [];
  const rows = grid?.rows ?? [];
  const values = useMemo(() => grid?.cells ?? [], [grid]);
  const format = data?.format ?? null;

  const share = useMemo(
    () => shareOfPeak(values.map((cell) => cell.value ?? 0)),
    [values],
  );

  // A cell by position, so the dense grid can ask for one that is not there.
  const byPosition = useMemo(() => {
    const map = new Map();
    values.forEach((cell) => { if (cell.x >= 0) map.set(`${cell.y}:${cell.x}`, cell); });
    return map;
  }, [values]);

  if (!columns.length || !values.length) return null;

  const hue = seriesColor(0, accent);
  const showRowLabels = variant === 'matrix' && rows.length > 0;

  /*
    One grid for labels and cells, not three stacked ones.

    The row labels, the column labels and the cells have to line up, and three
    separate grids line up only by coincidence — the moment a label is wider
    than its cell the whole column drifts. Here the label column is the grid's
    own first track and the header its first row, so alignment is structural.
  */
  const gridStyle = {
    gridTemplateColumns: `${showRowLabels ? 'auto ' : ''}repeat(${columns.length}, minmax(0, ${CELL_MAX_PX}px))`,
  };
  return (
    <div className={`flex flex-col items-start gap-1.5 ${className}`.trim()}>
      <div className="grid gap-[3px]" style={gridStyle}>
        {/* Header row: the corner under the row labels, then the columns. */}
        {showRowLabels ? <span /> : null}
        {columns.map((column, index) => (
          <span
            key={`head-${column}-${index}`}
            className="truncate text-center text-[9px] leading-none text-content-muted"
          >
            {column}
          </span>
        ))}

        {Array.from({ length: Math.max(1, rows.length) }).map((_, rowIndex) => (
          <Fragment key={rows[rowIndex] ?? rowIndex}>
            {showRowLabels ? (
              <span className="self-center whitespace-nowrap pr-1 text-right text-[9px] leading-none text-content-muted">
                {rows[rowIndex]}
              </span>
            ) : null}

            {columns.map((column, columnIndex) => {
              const cell = byPosition.get(`${rowIndex}:${columnIndex}`);
              const index = rowIndex * columns.length + columnIndex;
              const enter = enterAt(index, 'scale');
              const rowLabel = rows[rowIndex] ? `${rows[rowIndex]} ` : '';

              // No cell is a hole, not a zero.
              if (!cell || cell.value === null || cell.value === undefined) {
                return (
                  <div
                    key={`${rowIndex}-${columnIndex}`}
                    aria-hidden
                    className="aspect-square w-full rounded-[3px] bg-[rgb(var(--wg-wash))]"
                  />
                );
              }

              // Against its own quota when it has one, otherwise against the peak.
              const ratio = cell.ratio ?? share(cell.value);
              const reading = cell.ratio != null
                ? `${formatValue(cell.value, format)} of ${formatValue(cell.limit, format)} — ${formatRatio(cell.ratio, { signed: false, decimals: 0 })}`
                : `${bandFor(ratio)} — ${Math.round(ratio * 100)}% of peak`;
              const background = cell.token
                ? colourForToken(cell.token, hue)
                : chartAlpha(hue, (0.12 + Math.min(1, ratio) * 0.8).toFixed(2));

              return (
                <ChartDataPoint
                  key={`${rowIndex}-${columnIndex}`}
                  fill="block w-full"
                  label={`${rowLabel}${cell.label ?? column} · ${reading}`}
                >
                  <div
                    className={`flex aspect-square w-full items-center justify-center rounded-[3px] ${enter.className}`}
                    style={{ ...enter.style, background }}
                  >
                    {/*
                      The figure, in the cell. A shaded square alone says only
                      "darker is more"; the number is what the reader came for,
                      and leaving it in a tooltip means scrubbing sixty cells
                      to find one.

                      White ink above 55% shade: below that the dark text wins
                      the contrast, above it the light one does.
                    */}
                    {showValues ? (
                      <span
                        className={`text-[9px] font-semibold leading-none ${ratio > 0.55 ? 'text-white' : 'text-content'}`}
                      >
                        {formatValue(cell.value, format, '')}
                      </span>
                    ) : null}
                  </div>
                </ChartDataPoint>
              );
            })}
          </Fragment>
        ))}
      </div>

      <WidgetPeriodChip period={data?.period} />
    </div>
  );
}

HeatMapWidget.propTypes = {
  /** `HEATMAP`'s axes and cells, `USAGE_MAP`'s resources, or the dense pre-v2 grid. */
  data: PropTypes.shape({
    cells: PropTypes.arrayOf(PropTypes.object),
    x_categories: PropTypes.arrayOf(PropTypes.string),
    y_categories: PropTypes.arrayOf(PropTypes.string),
    min: PropTypes.number,
    max: PropTypes.number,
    format: PropTypes.object,
    unit_label: PropTypes.string,
    period: PropTypes.object,
    // pre-v2
    columns: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }).isRequired,
  variant: PropTypes.oneOf(['matrix', 'calendar']),
  showValues: PropTypes.bool,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(HeatMapWidget);
